import React from 'react';
import { connect } from 'react-redux';
import _ from 'underscore';
import Logger, { LOG_LEVELS } from '../../utils/logger';
import HelpText from '../inAppMessages/HelpText';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import LANGUAGE_CONTROLLER from '../../controllers/languages';
import FullStoryFieldCheck from './fullStoryExclusionChecker';
import { withFullStoryExclude } from '../../utils/fullStoryHelper';
import { SSelect } from '@avalara/skylab-sdk/react';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';

const log = new Logger('USER_QUESTION', LOG_LEVELS.ERROR);

// Helper functions to reduce cognitive complexity

const getSensitiveClassName = (isSensitive, additionalClass = '') => {
  if (isSensitive) {
    return withFullStoryExclude(additionalClass);
  }
  return additionalClass;
};

const createSyntheticEvent = (value) => ({
  target: { value }
});

const createSSelectHandler = (onFieldChange) => (e) => {
  onFieldChange(createSyntheticEvent(e.detail.item.value));
};

const createDropdownOptions = (dropDownValues, answer) => {
  const emptyOption = { key: '', value: '', label: '' };
  const mappedOptions = dropDownValues.map((dropDownValue) => {
    const key = dropDownValue.key || dropDownValue.displayString;
    return {
      key,
      value: key,
      label: dropDownValue.displayString,
      selected: answer === key
    };
  });
  return [emptyOption, ...mappedOptions];
};

const createYesNoOptions = (languageString, answer) => [
  {
    key: '0',
    value: '0',
    label: LANGUAGE_CONTROLLER.format(languageString, 'userQuestions_no'),
    selected: answer === '0'
  },
  {
    key: '1',
    value: '1',
    label: LANGUAGE_CONTROLLER.format(languageString, 'userQuestions_yes'),
    selected: answer === '1'
  }
];

const parseDate = (answer) => {
  const dateTime = new Date(answer || '');
  return isNaN(dateTime.getTime()) ? '' : dateTime.toISOString().split('T')[0];
};

const splitPeriodDates = (answer) => {
  const parts = answer.split('*');
  return {
    startDate: parts[0] || '',
    endDate: parts[1] || ''
  };
};

// Field type renderers

const renderHiddenField = (question, answer, isSensitive) => (
  <input 
    name={question.BoxID} 
    type="hidden" 
    className={getSensitiveClassName(isSensitive, 'disabled')} 
    value={answer} 
    readOnly 
  />
);

const renderDropdownField = (question, answer, isSensitive, onFieldChange) => {
  const dropdownOptions = createDropdownOptions(question.DropDownValues, answer);
  
  return (
    <SSelect
      inputId={`dropdown-${question.BoxID}`}
      name={question.BoxID}
      className={getSensitiveClassName(isSensitive)}
      optionsList={dropdownOptions}
      multiple={false}
      onS-select={createSSelectHandler(onFieldChange)}
    />
  );
};

const renderTextField = (question, answer, isSensitive, onFieldChange) => (
  <span>
    <input 
      name={question.BoxID} 
      className={getSensitiveClassName(isSensitive)} 
      type="text" 
      placeholder="text" 
      onChange={onFieldChange} 
      value={answer} 
    />
  </span>
);

const renderTextFieldType0 = (question, answer, isSensitive, onFieldChange) => {
  if (question.Visible === false) {
    return renderHiddenField(question, answer, isSensitive);
  }
  
  if (!_.isEmpty(question.DropDownValues) && _.isArray(question.DropDownValues)) {
    return renderDropdownField(question, answer, isSensitive, onFieldChange);
  }
  
  return renderTextField(question, answer, isSensitive, onFieldChange);
};

const renderNumberField = (question, answer, isSensitive, onFieldChange) => (
  <input 
    name={question.BoxID} 
    className={getSensitiveClassName(isSensitive)} 
    type="number" 
    placeholder="number" 
    onChange={onFieldChange} 
    value={answer} 
  />
);

const renderDateField = (question, answer, isSensitive, onFieldChange) => {
  const date = parseDate(answer);
  
  if (question.Visible === false) {
    return (
      <input 
        name={question.BoxID} 
        className={getSensitiveClassName(isSensitive, 'disabled')} 
        type="hidden" 
        value={date} 
        readOnly 
      />
    );
  }
  
  return (
    <input 
      name={question.BoxID} 
      className={getSensitiveClassName(isSensitive)} 
      type="date" 
      placeholder="date" 
      onChange={onFieldChange} 
      value={date} 
    />
  );
};

const renderYesNoField = (question, answer, isSensitive, onFieldChange, languageString) => {
  const yesNoOptions = createYesNoOptions(languageString, answer);
  
  return (
    <SSelect
      inputId={`yesno-${question.BoxID}`}
      name={question.BoxID}
      className={getSensitiveClassName(isSensitive)}
      optionsList={yesNoOptions}
      multiple={false}
      onS-select={createSSelectHandler(onFieldChange)}
    />
  );
};

const renderPeriodField = (question, answer, onPeriodStartUpdate, onPeriodEndUpdate) => {
  const { startDate, endDate } = splitPeriodDates(answer);
  
  return (
    <s-row>
      <s-col span="6">
        <label><LanguageStringResolver string="userQuestions_Start" /></label>
        <input 
          name={question.BoxID} 
          type="date" 
          placeholder="startDate" 
          onChange={onPeriodStartUpdate} 
          value={startDate}
        />
      </s-col>
      <s-col span="6">
        <label><LanguageStringResolver string="userQuestions_end" /></label>
        <input 
          name={question.BoxID} 
          type="date" 
          placeholder="endDate" 
          onChange={onPeriodEndUpdate} 
          value={endDate} 
        />
      </s-col>
    </s-row>
  );
};

const renderPredefinedTextField = (question, answer, isSensitive, onFieldChange) => (
  <input 
    name={question.BoxID} 
    className={getSensitiveClassName(isSensitive)} 
    type="text" 
    placeholder="text/predefined" 
    onChange={onFieldChange} 
    value={answer} 
  />
);

const renderDefaultField = (question, answer, isSensitive, onFieldChange) => (
  <input 
    name={question.BoxID} 
    className={getSensitiveClassName(isSensitive)} 
    type="text" 
    onChange={onFieldChange} 
    value={answer} 
  />
);

const renderFieldByType = (question, answer, isSensitive, onFieldChange, languageString, onPeriodStartUpdate, onPeriodEndUpdate) => {
  switch (question.FieldType) {
    case 0:
      return renderTextFieldType0(question, answer, isSensitive, onFieldChange);
    case 1:
      return renderNumberField(question, answer, isSensitive, onFieldChange);
    case 2:
      return renderDateField(question, answer, isSensitive, onFieldChange);
    case 3:
      return renderYesNoField(question, answer, isSensitive, onFieldChange, languageString);
    case 4:
      return renderPeriodField(question, answer, onPeriodStartUpdate, onPeriodEndUpdate);
    case 5:
      return renderPredefinedTextField(question, answer, isSensitive, onFieldChange);
    default:
      return renderDefaultField(question, answer, isSensitive, onFieldChange);
  }
};

const UserQuestion = ({ question, answer, onFieldChange, filingTypeId, languageString }) => {
  const onPeriodStartUpdate = (evt) => {
    const endDate = answer.split('*')[1];
    onFieldChange(createSyntheticEvent([evt.target.value, endDate].join('*')));
  };

  const onPeriodEndUpdate = (evt) => {
    const startDate = answer.split('*')[0];
    onFieldChange(createSyntheticEvent([startDate, evt.target.value].join('*')));
  };

  log.debug('render(): ', { question, answer, onFieldChange, filingTypeId });

  const isSensitive = FullStoryFieldCheck.isSensitive(question.BoxID, filingTypeId);
  const isPublicCommentAvailable = !_.isEmpty(question.PublicComment);

  if (question.hasOwnProperty('Visible') && question.Visible === false) {
    return null;
  }

  return (
    <ReadOnlyWrapper>
      <div>
        <span>
          <label className="mt10">
            {question.Question}
            {isPublicCommentAvailable && <HelpText id={`hint_${question.BoxID}`} helpText={question.PublicComment} />}
          </label>
        </span>
        <span className="clear"></span>
        {renderFieldByType(question, answer, isSensitive, onFieldChange, languageString, onPeriodStartUpdate, onPeriodEndUpdate)}
      </div>
    </ReadOnlyWrapper>
  );
};


const mapStateToProps = (state) => ({
  languages: state.languages,
});

export default connect(mapStateToProps)(UserQuestion);
